import { Skeleton } from '@/components/ui/skeleton';
import { TableCell, TableRow } from '@/components/ui/table';
import { t } from '@lingui/macro';

interface TableSkeletonProps {
  /** Number of placeholder body rows to render. */
  rows?: number;
  /** Number of placeholder columns per row. */
  columns?: number;
  className?: string;
}

/**
 * Table-shaped loading placeholder for list pages.
 *
 * Renders a toolbar row, a header row, and `rows` body rows of `columns` cells so
 * that a list page can keep its own header and action bar mounted while the data
 * loads (in-place loading) instead of blanking the whole screen behind a
 * full-page spinner. Follows the same Skeleton + role="status" pattern as
 * RelationshipsSkeleton.
 */
export function TableSkeleton({ rows = 8, columns = 6, className }: TableSkeletonProps) {
  return (
    <div
      className={`space-y-3 ${className || ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t({ message: 'Loading table', id: 'table.skeleton.loading' })}
    >
      {/* Toolbar row (search field + action) */}
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-9 w-64" aria-hidden="true" />
        <Skeleton className="h-9 w-32" aria-hidden="true" />
      </div>

      {/* Header row */}
      <div className="flex gap-3 border-b pb-2">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${i}`} className="h-4 flex-1" aria-hidden="true" />
        ))}
      </div>

      {/* Body rows */}
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={`row-${r}`} className="flex gap-3 py-2">
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton key={`cell-${r}-${c}`} className="h-4 flex-1" aria-hidden="true" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface TableSkeletonRowsProps {
  /** Number of placeholder rows. */
  rows: number;
  /** Number of cells per row; match the table's visible column count. */
  columns: number;
  /** Classes for each cell, so the placeholder rows match the table's row height. */
  cellClassName?: string;
}

/**
 * Placeholder body rows for a table whose header and toolbar stay mounted while
 * the first page loads. Renders `<tr>` elements, so it goes inside a `<tbody>`;
 * the caller marks the body `aria-busy`.
 */
export function TableSkeletonRows({ rows, columns, cellClassName }: TableSkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={`skeleton-row-${r}`} className="hover:bg-transparent" data-testid="table-skeleton-row">
          {Array.from({ length: columns }).map((_, c) => (
            <TableCell key={`skeleton-cell-${r}-${c}`} className={cellClassName}>
              <Skeleton className="h-4 w-full" aria-hidden="true" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
